import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Autocomplete,
  Stack,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import SecurityIcon from '@mui/icons-material/Security'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import useAuthStore from '../store/authStore'
import { adminWhitelistService, adminAccessService, authService } from '../services'
import { isValidAdminWhitelistEmail } from '../utils/adminWhitelistEmail'
import {
  hasAdminPermission,
  PERMISSION_SUBJECTS,
  summarizePermissionsForHumans,
  ADMIN_ROLE_CODES,
} from '../utils/adminPermissions'
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_THROTTLE_MS,
  useDebouncedThrottledValue,
} from '../hooks/useDebouncedThrottledValue'
import RolePermissionsEditor from '../components/admin/RolePermissionsEditor'
import ManageRolesDialog from '../components/admin/ManageRolesDialog'

const mergePermissionsFromRoleCodes = (catalog, roleCodes) => {
  const out = {}
  for (const code of roleCodes || []) {
    const role = (catalog || []).find((r) => r.code === code)
    if (!role?.permissions) continue
    if (role.permissions.all?.manage) {
      return { all: { manage: true } }
    }
    for (const [subject, actions] of Object.entries(role.permissions)) {
      if (!out[subject]) out[subject] = {}
      for (const [action, val] of Object.entries(actions || {})) {
        out[subject][action] = !!(out[subject][action] || val)
      }
    }
  }
  return out
}

const UserPermissionsPage = () => {
  const navigate = useNavigate()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const canReadUsers = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ADMIN_WHITELIST,
    'read',
  )
  const canWriteUsers = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ADMIN_WHITELIST,
    'write',
  )
  const canReadRoles = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ADMIN_USER_ROLES,
    'read',
  )
  const canWriteRoles = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ADMIN_USER_ROLES,
    'write',
  )
  const canOpenPage = canReadUsers || canReadRoles
  const setSessionFromVerify = useAuthStore((s) => s.setSessionFromVerify)

  const [whitelistRows, setWhitelistRows] = useState([])
  const [whitelistPage, setWhitelistPage] = useState(0)
  const [whitelistRowsPerPage, setWhitelistRowsPerPage] = useState(10)
  const [whitelistTotal, setWhitelistTotal] = useState(0)
  const [whitelistSearchInput, setWhitelistSearchInput] = useState('')
  const whitelistSearch = useDebouncedThrottledValue(
    whitelistSearchInput,
    SEARCH_DEBOUNCE_MS,
    SEARCH_THROTTLE_MS,
  )
  const [whitelistQuerySnapshot, setWhitelistQuerySnapshot] = useState(whitelistSearch)
  const [whitelistLoading, setWhitelistLoading] = useState(canReadUsers)
  const whitelistRequestRef = useRef(0)
  const [usersData, setUsersData] = useState(null)
  const [usersPage, setUsersPage] = useState(0)
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10)
  const [usersTotal, setUsersTotal] = useState(0)
  const [usersSearchInput, setUsersSearchInput] = useState('')
  const usersSearch = useDebouncedThrottledValue(
    usersSearchInput,
    SEARCH_DEBOUNCE_MS,
    SEARCH_THROTTLE_MS,
  )
  /** Selected role catalog rows for Accounts & roles multi-filter (any-match). */
  const [usersRoleFilter, setUsersRoleFilter] = useState([])
  const [usersQuerySnapshot, setUsersQuerySnapshot] = useState(usersSearch)
  const [usersLoading, setUsersLoading] = useState(canReadRoles)
  const usersRequestRef = useRef(0)
  const [error, setError] = useState(null)
  const [snackbar, setSnackbar] = useState(null)

  const [addOpen, setAddOpen] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addName, setAddName] = useState('')
  const [addEmailError, setAddEmailError] = useState('')
  const [addSubmitting, setAddSubmitting] = useState(false)

  const [rolesOpen, setRolesOpen] = useState(false)
  const [rolesTarget, setRolesTarget] = useState(null)
  const [rolesContext, setRolesContext] = useState(null)
  const [selectedRoleCodes, setSelectedRoleCodes] = useState([])
  const [rolesSaving, setRolesSaving] = useState(false)

  // Top-level role catalog & permission schema (loaded on mount, independent of per-user editor)
  const [roleCatalog, setRoleCatalog] = useState([])
  const [permissionSchema, setPermissionSchema] = useState([])
  const [rolePresets, setRolePresets] = useState([])

  // Create Role dialog state
  const [createRoleOpen, setCreateRoleOpen] = useState(false)
  const [createRoleName, setCreateRoleName] = useState('')
  const [createRolePermissions, setCreateRolePermissions] = useState({})
  const [createRoleSaving, setCreateRoleSaving] = useState(false)
  const [manageRolesOpen, setManageRolesOpen] = useState(false)

  if (whitelistSearch !== whitelistQuerySnapshot) {
    setWhitelistQuerySnapshot(whitelistSearch)
    setWhitelistPage(0)
  }
  if (usersSearch !== usersQuerySnapshot) {
    setUsersQuerySnapshot(usersSearch)
    setUsersPage(0)
  }

  const refreshVerify = useCallback(async () => {
    try {
      const v = await authService.verifyAdmin()
      setSessionFromVerify(v)
    } catch {
      /* ignore */
    }
  }, [setSessionFromVerify])

  const loadWhitelist = useCallback(async () => {
    if (!canReadUsers) return
    const requestId = whitelistRequestRef.current + 1
    whitelistRequestRef.current = requestId
    try {
      setWhitelistLoading(true)
      const data = await adminWhitelistService.getWhitelist({
        page: whitelistPage + 1,
        limit: whitelistRowsPerPage,
        search: whitelistSearch.trim() || undefined,
      })
      if (requestId !== whitelistRequestRef.current) return
      const total = data.pagination?.total || 0
      const lastPage = Math.max(0, Math.ceil(total / whitelistRowsPerPage) - 1)
      if (whitelistPage > lastPage) {
        setWhitelistPage(lastPage)
        return
      }
      setWhitelistRows(data.items || [])
      setWhitelistTotal(total)
    } catch (err) {
      if (requestId !== whitelistRequestRef.current) return
      const msg =
        err.response?.data?.message ||
        err.response?.data?.code ||
        'Failed to load access data'
      setError(typeof msg === 'string' ? msg : 'Failed to load access data')
    } finally {
      if (requestId === whitelistRequestRef.current) setWhitelistLoading(false)
    }
  }, [canReadUsers, whitelistPage, whitelistRowsPerPage, whitelistSearch])

  const usersRoleCodesKey = usersRoleFilter
    .map((r) => r?.code)
    .filter(Boolean)
    .sort()
    .join(',')

  const loadUsers = useCallback(async () => {
    if (!canReadRoles) return
    const requestId = usersRequestRef.current + 1
    usersRequestRef.current = requestId
    try {
      setUsersLoading(true)
      const roleCodes = usersRoleCodesKey
        ? usersRoleCodesKey.split(',')
        : undefined
      const data = await adminAccessService.listGoogleUsers({
        page: usersPage + 1,
        limit: usersRowsPerPage,
        search: usersSearch.trim() || undefined,
        roleCodes: roleCodes?.length ? roleCodes.join(',') : undefined,
      })
      if (requestId !== usersRequestRef.current) return
      const total = data.pagination?.total || 0
      const lastPage = Math.max(0, Math.ceil(total / usersRowsPerPage) - 1)
      if (usersPage > lastPage) {
        setUsersPage(lastPage)
        return
      }
      setUsersData(data)
      setUsersTotal(total)
    } catch (err) {
      if (requestId !== usersRequestRef.current) return
      const msg =
        err.response?.data?.message ||
        err.response?.data?.code ||
        'Failed to load access data'
      setError(typeof msg === 'string' ? msg : 'Failed to load access data')
    } finally {
      if (requestId === usersRequestRef.current) setUsersLoading(false)
    }
  }, [canReadRoles, usersPage, usersRowsPerPage, usersSearch, usersRoleCodesKey])

  const loadCatalog = useCallback(async () => {
    try {
      const ctx = await adminAccessService.getAssignableContext()
      setRoleCatalog(ctx.catalog || [])
      setPermissionSchema(ctx.permissionSchema || [])
      setRolePresets(ctx.rolePresets || [])
    } catch {
      // non-fatal — catalog is used for Create Role UI only
    }
  }, [])

  useEffect(() => {
    if (!canOpenPage) {
      navigate('/dashboard', { replace: true })
    }
  }, [canOpenPage, navigate])

  useEffect(() => {
    if (!canReadUsers) return
    loadWhitelist()
  }, [canReadUsers, loadWhitelist])

  useEffect(() => {
    if (!canReadRoles) return
    loadUsers()
  }, [canReadRoles, loadUsers])

  useEffect(() => {
    if (!canReadRoles) return
    loadCatalog()
  }, [canReadRoles, loadCatalog])

  const handleAddWhitelist = async () => {
    const email = addEmail.trim()
    if (!email) {
      setAddEmailError('Please enter a valid email address.')
      return
    }
    if (!isValidAdminWhitelistEmail(email)) {
      setAddEmailError('Please enter a valid email address.')
      return
    }
    if (email.includes('+')) {
      setAddEmailError('Plus-aliases are not allowed for admin accounts.')
      return
    }
    setAddEmailError('')
    try {
      setAddSubmitting(true)
      const result = await authService.provisionInvite({
        email,
        name: addName.trim() || undefined,
      })
      const msg =
        result.status === 'already_sent'
          ? 'Email has already been sent. Please check inbox or spam.'
          : result.status === 'send_failed'
            ? result.message || "Invite created but email couldn't be sent. Try resend."
            : result.message || 'Invitation sent'
      setSnackbar({
        severity: result.status === 'send_failed' ? 'warning' : 'success',
        message: msg,
      })
      setAddOpen(false)
      setAddEmail('')
      setAddName('')
      setAddEmailError('')
      if (whitelistPage !== 0) setWhitelistPage(0)
      else await loadWhitelist()
      if (usersPage !== 0) setUsersPage(0)
      else await loadUsers()
      await refreshVerify()
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: err.response?.data?.message || 'Could not invite admin',
      })
    } finally {
      setAddSubmitting(false)
    }
  }

  const closeAddDialog = () => {
    if (addSubmitting) return
    setAddOpen(false)
    setAddEmail('')
    setAddName('')
    setAddEmailError('')
  }

  const handleRemoveWhitelist = async (email) => {
    if (!window.confirm(`Remove access for ${email}?`)) return
    try {
      await adminWhitelistService.removeFromWhitelist(email)
      setSnackbar({ severity: 'success', message: 'Access updated' })
      await loadWhitelist()
      await loadUsers()
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: err.response?.data?.message || 'Could not remove',
      })
    }
  }

  const openRolesEditor = async (row) => {
    try {
      setError(null)
      const ctx = await adminAccessService.getAssignableContext(row.id)
      setRolesTarget(row)
      setRolesContext(ctx)
      const codes = ctx.target?.roleCodes || row.roleCodes || []
      setSelectedRoleCodes([...codes])
      setRolesOpen(true)
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.code || 'Could not load roles'
      setSnackbar({ severity: 'error', message: typeof msg === 'string' ? msg : 'Could not load roles' })
    }
  }

  const toggleRole = (code, checked, assignableSet) => {
    if (!assignableSet.has(code)) return
    setSelectedRoleCodes((prev) => {
      if (checked) return [...new Set([...prev, code])]
      return prev.filter((c) => c !== code)
    })
  }

  const saveRoles = async () => {
    if (!rolesTarget) return
    try {
      setRolesSaving(true)
      await adminAccessService.setUserRoles(rolesTarget.id, selectedRoleCodes)
      setSnackbar({ severity: 'success', message: 'Roles saved' })
      setRolesOpen(false)
      await loadUsers()
      await refreshVerify()
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: err.response?.data?.message || err.response?.data?.code || 'Could not save roles',
      })
    } finally {
      setRolesSaving(false)
    }
  }

  const openCreateRoleDialog = () => {
    setCreateRoleName('')
    setCreateRolePermissions({})
    setCreateRoleOpen(true)
  }

  const saveCreateRole = async () => {
    const displayName = createRoleName.trim()
    if (!displayName) return

    try {
      setCreateRoleSaving(true)
      await adminAccessService.createRole({
        display_name: displayName,
        permissions: createRolePermissions,
      })

      setSnackbar({ severity: 'success', message: 'Role created' })
      setCreateRoleOpen(false)
      await loadCatalog()

      if (rolesTarget) {
        const targetCtx = await adminAccessService.getAssignableContext(rolesTarget.id)
        setRolesContext(targetCtx)
        const codes = targetCtx.target?.roleCodes || []
        setSelectedRoleCodes([...codes])
      }
    } catch (err) {
      setSnackbar({
        severity: 'error',
        message: err.response?.data?.message || err.response?.data?.code || 'Could not create role',
      })
    } finally {
      setCreateRoleSaving(false)
    }
  }

  if (!canOpenPage) {
    return null
  }

  const assignableSet = new Set(rolesContext?.assignableRoleCodes || [])
  const catalog = rolesContext?.catalog || roleCatalog
  const selectedEffectivePermissions = mergePermissionsFromRoleCodes(
    catalog,
    selectedRoleCodes,
  )
  const selectedEffectiveSummary = summarizePermissionsForHumans(
    permissionSchema,
    selectedEffectivePermissions,
  )
  const hasSuperuserSelected = selectedRoleCodes.includes(ADMIN_ROLE_CODES.SUPERUSER)
  const onlyUserPermissionsSelected =
    !hasSuperuserSelected &&
    selectedRoleCodes.length > 0 &&
    selectedRoleCodes.every((code) => code === ADMIN_ROLE_CODES.PORTAL_ACCESS_CONTROL)

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
        User permissions
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Manage who may sign in to this portal and which administrative roles they hold.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {canReadUsers && (
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Authorized sign-in list
              </Typography>
            </Box>
            {canWriteUsers && (
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAddOpen(true)}>
                Invite admin
              </Button>
            )}
          </Box>
          <TextField
            size="small"
            placeholder="Search email or name"
            value={whitelistSearchInput}
            onChange={(e) => setWhitelistSearchInput(e.target.value)}
            sx={{ mb: 2, minWidth: 280 }}
            InputProps={{
              endAdornment: whitelistSearchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear authorized sign-in search"
                    onClick={() => setWhitelistSearchInput('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8f9fb' }}>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  {canWriteUsers && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {whitelistLoading ? (
                  <TableRow>
                    <TableCell colSpan={canWriteUsers ? 3 : 2} align="center">
                      <CircularProgress size={22} sx={{ my: 1 }} />
                    </TableCell>
                  </TableRow>
                ) : whitelistRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canWriteUsers ? 3 : 2}>
                      <Typography variant="body2" color="text.secondary">
                        {whitelistSearch.trim() ? 'No entries match this search.' : 'No entries yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  whitelistRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.name || '—'}</TableCell>
                      {canWriteUsers && (
                        <TableCell align="right">
                          <Button size="small" color="error" onClick={() => handleRemoveWhitelist(row.email)}>
                            Remove
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={whitelistTotal}
            page={whitelistPage}
            onPageChange={(_, nextPage) => setWhitelistPage(nextPage)}
            rowsPerPage={whitelistRowsPerPage}
            onRowsPerPageChange={(e) => {
              setWhitelistRowsPerPage(parseInt(e.target.value, 10))
              setWhitelistPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>
      )}

      {canReadRoles && (
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Accounts & roles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select a user to view effective permissions and assign roles you are allowed to manage.
              </Typography>
              {canReadRoles && roleCatalog?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {roleCatalog.length} roles available
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {canReadRoles && (
                <Button
                  variant="outlined"
                  startIcon={<ManageAccountsIcon />}
                  onClick={() => setManageRolesOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Manage roles
                </Button>
              )}
              {canWriteRoles && (
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={openCreateRoleDialog}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Create role
                </Button>
              )}
            </Box>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}
            alignItems={{ sm: 'flex-start' }}
          >
            <TextField
              size="small"
              placeholder="Search email or name"
              value={usersSearchInput}
              onChange={(e) => setUsersSearchInput(e.target.value)}
              sx={{ minWidth: 280, flex: 1 }}
              InputProps={{
                endAdornment: usersSearchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear Google account search"
                      onClick={() => setUsersSearchInput('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              sx={{ minWidth: 280, flex: 1.2 }}
              options={roleCatalog || []}
              value={usersRoleFilter}
              onChange={(_, value) => {
                setUsersRoleFilter(value || [])
                setUsersPage(0)
              }}
              getOptionLabel={(option) =>
                option?.display_name
                  ? `${option.display_name} (${option.code})`
                  : option?.code || ''
              }
              isOptionEqualToValue={(a, b) => a?.code === b?.code}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props
                return (
                  <li key={key || option.code} {...rest}>
                    <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.display_name || option.code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.code}
                      </Typography>
                    </Box>
                  </li>
                )
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.code}
                    size="small"
                    label={option.display_name || option.code}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by role"
                  placeholder={
                    usersRoleFilter.length ? '' : 'Any of selected roles'
                  }
                />
              )}
            />
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8f9fb' }}>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Whitelisted</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={22} sx={{ my: 1 }} />
                    </TableCell>
                  </TableRow>
                ) : (usersData?.items || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        {usersSearch.trim() || usersRoleFilter.length
                          ? 'No Google accounts match this search or role filter.'
                          : 'No Google accounts found yet. Users appear after they sign in at least once.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (usersData?.items || []).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.name || '—'}</TableCell>
                      <TableCell>{row.whitelisted ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(row.roleCodes || []).map((c) => (
                            <Chip key={c} label={c} size="small" variant="outlined" />
                          ))}
                          {(!row.roleCodes || row.roleCodes.length === 0) && row.whitelisted && (
                            <Chip label="Implicit admin" size="small" color="default" variant="filled" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => openRolesEditor(row)}
                          disabled={
                            (row.roleCodes || []).includes('SUPERUSER') && !isSuperuserActor
                          }
                        >
                          Roles
                        </Button>
                        {canWriteUsers && (
                          <Button
                            size="small"
                            onClick={async () => {
                              try {
                                const r = await authService.resendInvite(row.id)
                                setSnackbar({
                                  severity: r.status === 'send_failed' ? 'warning' : 'success',
                                  message: r.message || 'Invite resent',
                                })
                              } catch (err) {
                                setSnackbar({
                                  severity: 'error',
                                  message: err.response?.data?.message || 'Resend failed',
                                })
                              }
                            }}
                          >
                            Resend invite
                          </Button>
                        )}
                        {canWriteRoles && row.whitelisted ? (
                          <Button
                            size="small"
                            color="warning"
                            onClick={async () => {
                              if (!window.confirm(`Deactivate ${row.email}?`)) return
                              try {
                                await authService.deactivateUser(row.id)
                                setSnackbar({ severity: 'success', message: 'Account deactivated' })
                                await loadUsers()
                                await loadWhitelist()
                              } catch (err) {
                                setSnackbar({
                                  severity: 'error',
                                  message: err.response?.data?.message || 'Deactivate failed',
                                })
                              }
                            }}
                          >
                            Deactivate
                          </Button>
                        ) : canWriteRoles ? (
                          <Button
                            size="small"
                            color="success"
                            onClick={async () => {
                              try {
                                await authService.reactivateUser(row.id)
                                setSnackbar({ severity: 'success', message: 'Account reactivated' })
                                await loadUsers()
                                await loadWhitelist()
                              } catch (err) {
                                setSnackbar({
                                  severity: 'error',
                                  message: err.response?.data?.message || 'Reactivate failed',
                                })
                              }
                            }}
                          >
                            Reactivate
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={usersTotal}
            page={usersPage}
            onPageChange={(_, nextPage) => setUsersPage(nextPage)}
            rowsPerPage={usersRowsPerPage}
            onRowsPerPageChange={(e) => {
              setUsersRowsPerPage(parseInt(e.target.value, 10))
              setUsersPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>
      )}

      <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Invite admin</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={addEmail}
            error={!!addEmailError}
            helperText={addEmailError}
            onChange={(e) => {
              setAddEmail(e.target.value)
              if (addEmailError) setAddEmailError('')
            }}
            onBlur={() => {
              const email = addEmail.trim()
              if (email && !isValidAdminWhitelistEmail(email)) {
                setAddEmailError('Please enter a valid email address.')
              }
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Display name (optional)"
            fullWidth
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog} disabled={addSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddWhitelist} disabled={addSubmitting}>
            {addSubmitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rolesOpen} onClose={() => !rolesSaving && setRolesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Roles for {rolesTarget?.email}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Roles add together. Someone can have more than one role — they get everything those roles allow.
          </Alert>
          {rolesContext?.target?.lockedRoleCodes?.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Some roles on this account can only be changed by a Superuser.
            </Alert>
          )}
          {hasSuperuserSelected && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Superuser has full access to everything. Other roles are unnecessary for this person.
            </Alert>
          )}
          {onlyUserPermissionsSelected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This only lets them manage who can sign in and assign roles — not Orders, Shops, or the rest of the portal. Add another role if they need those areas.
            </Alert>
          )}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Choose roles
          </Typography>
          <FormGroup>
            {catalog.map((role) => {
              const canToggle = assignableSet.has(role.code)
              const checked = selectedRoleCodes.includes(role.code)
              const summary = summarizePermissionsForHumans(
                permissionSchema,
                role.permissions,
              )
              const isSuper = role.code === ADMIN_ROLE_CODES.SUPERUSER
              return (
                <Box key={role.code} sx={{ mb: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={!canWriteRoles || !canToggle}
                        onChange={(e) => {
                          if (isSuper && e.target.checked) {
                            setSelectedRoleCodes([role.code])
                            return
                          }
                          toggleRole(role.code, e.target.checked, assignableSet)
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {role.display_name}
                          {isSuper && (
                            <Chip
                              component="span"
                              label="Full access"
                              size="small"
                              color="success"
                              sx={{ ml: 1, height: 22 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {summary.slice(0, 2).join(' · ')}
                          {summary.length > 2 ? '…' : ''}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )
            })}
          </FormGroup>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Combined access with current selection
          </Typography>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {selectedEffectiveSummary.map((line) => (
                <Typography component="li" variant="body2" key={line} sx={{ mb: 0.5 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolesOpen(false)} disabled={rolesSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveRoles} disabled={rolesSaving || !canWriteRoles}>
            {rolesSaving ? <CircularProgress size={22} /> : 'Save roles'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createRoleOpen}
        onClose={() => !createRoleSaving && setCreateRoleOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create a role</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose what people with this role can see and change. Signing in is controlled separately by the authorized sign-in list above.
          </Typography>
          <TextField
            fullWidth
            label="Role name"
            placeholder="e.g. Warehouse team"
            value={createRoleName}
            onChange={(e) => setCreateRoleName(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          {permissionSchema.length === 0 ? (
            <Alert severity="info">Permission options are still loading…</Alert>
          ) : (
            <RolePermissionsEditor
              permissionSchema={permissionSchema}
              rolePresets={rolePresets}
              selectedPermissions={createRolePermissions}
              onChange={setCreateRolePermissions}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateRoleOpen(false)} disabled={createRoleSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveCreateRole}
            disabled={
              createRoleSaving ||
              !createRoleName.trim() ||
              Object.keys(createRolePermissions || {}).length === 0
            }
          >
            {createRoleSaving ? <CircularProgress size={22} /> : 'Create role'}
          </Button>
        </DialogActions>
      </Dialog>

      <ManageRolesDialog
        open={manageRolesOpen}
        onClose={() => setManageRolesOpen(false)}
        canWriteRoles={canWriteRoles}
        permissionSchema={permissionSchema}
        rolePresets={rolePresets}
        onRolesChanged={loadCatalog}
        onSnackbar={setSnackbar}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default UserPermissionsPage
